import { cn } from "@/lib/cn";

export type WordmarkSize = "sm" | "md" | "lg";

export interface WordmarkProps {
  /** Font-size of the mark; pulse geometry is em-based and scales with it. */
  size?: WordmarkSize;
  /** Renders the on-dark variant — paper-colored core dot + paper text,
   *  while the chartreuse + indigo rings stay branded. */
  onDark?: boolean;
  className?: string;
  /** Decorative by default. Pass an explicit label for screen readers when
   *  the mark is the only content of an interactive element. */
  "aria-label"?: string;
}

const SIZE: Record<WordmarkSize, string> = {
  sm: "text-base",      // ~16px — sidebar
  md: "text-2xl",       // ~24px — auth / app header
  lg: "text-[44px]",    // marketing hero
};

/**
 * Cawamba wordmark with sonar-pulsing dot inside the bowl of the C.
 * The CSS that drives the mark lives in `tokens-v2-wordmark.css`
 * (imported globally from `globals.css`). The animation honors
 * `prefers-reduced-motion` — at that point the dot rests on chartreuse.
 */
export function Wordmark({
  size = "md",
  onDark = false,
  className,
  "aria-label": ariaLabel,
}: WordmarkProps) {
  return (
    <span
      className={cn(
        "cawamba",
        onDark && "on-dark",
        SIZE[size],
        className,
      )}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    >
      <span className="c-glyph">
        C<span className="dot" aria-hidden />
      </span>
      awamba
    </span>
  );
}
